import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';

interface ComboboxOption {
    id: string;
    title: string;
    subtitle?: string;
    badge?: string;
    date?: string;
}

interface SearchableComboboxProps {
    options: ComboboxOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    icon?: React.ElementType;
    className?: string;
    emptyText?: string;
    isLoading?: boolean;
}

const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
    options,
    value,
    onChange,
    placeholder = "Selecteer...",
    searchPlaceholder = "Zoeken...",
    icon: Icon,
    className = "",
    emptyText = "Geen resultaten gevonden.",
    isLoading = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options.find(opt => opt.id === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            setSearchQuery('');
        }
    }, [isOpen]);

    const filteredOptions = options.filter(opt => {
        const searchLower = searchQuery.toLowerCase();
        return (
            opt.title.toLowerCase().includes(searchLower) ||
            (opt.subtitle && opt.subtitle.toLowerCase().includes(searchLower)) ||
            (opt.badge && opt.badge.toLowerCase().includes(searchLower))
        );
    });

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <button
                type="button"
                onClick={() => !isLoading && setIsOpen(!isOpen)}
                disabled={isLoading}
                className={`w-full bg-white border ${isOpen ? 'border-[#185ABD] ring-2 ring-[#185ABD]/10' : 'border-slate-200 hover:border-slate-300'} ${isLoading ? 'opacity-70 cursor-wait bg-slate-50' : ''} text-slate-700 text-sm font-bold rounded-lg px-4 py-2.5 outline-none transition-all flex items-center justify-between shadow-sm min-h-[42px]`}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {Icon && <Icon size={16} className={`${isLoading ? 'text-slate-300' : 'text-slate-400'} shrink-0`} />}
                    {isLoading ? (
                        <span className="text-slate-400 font-normal italic">Gegevens laden...</span>
                    ) : selectedOption ? (
                        <div className="flex flex-col items-start overflow-hidden text-left">
                            <span className="truncate w-full text-slate-800">{selectedOption.title}</span>
                            {(selectedOption.subtitle || selectedOption.badge) && (
                                <span className="text-[10px] text-slate-400 font-medium truncate w-full mt-0.5">
                                    {selectedOption.subtitle} {selectedOption.subtitle && selectedOption.badge && '•'} {selectedOption.badge}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-slate-400 font-normal">{placeholder}</span>
                    )}
                </div>
                {isLoading ? (
                    <div className="shrink-0 ml-2 animate-spin text-slate-400">
                        <svg className="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    </div>
                ) : (
                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''} shrink-0 ml-2`} />
                )}
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-fadeIn">
                    <div className="p-2 border-b border-slate-100 bg-slate-50/50">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-[#185ABD] focus:ring-1 focus:ring-[#185ABD] transition-all"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    
                    <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-1">
                        {filteredOptions.length === 0 ? (
                            <div className="p-4 text-center text-sm text-slate-500 italic">
                                {emptyText}
                            </div>
                        ) : (
                            filteredOptions.map((opt) => {
                                const isSelected = opt.id === value;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => {
                                            onChange(opt.id);
                                            setIsOpen(false);
                                        }}
                                        className={`w-full text-left px-3 py-2.5 rounded-lg flex items-start gap-3 transition-colors ${
                                            isSelected ? 'bg-[#185ABD]/5' : 'hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className={`mt-0.5 shrink-0 ${isSelected ? 'text-[#185ABD]' : 'text-transparent'}`}>
                                            <Check size={14} strokeWidth={3} />
                                        </div>
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className={`text-sm truncate ${isSelected ? 'font-bold text-[#185ABD]' : 'font-semibold text-slate-700'}`}>
                                                    {opt.title}
                                                </span>
                                                {opt.date && (
                                                    <span className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">
                                                        {opt.date}
                                                    </span>
                                                )}
                                            </div>
                                            {(opt.subtitle || opt.badge) && (
                                                <div className="flex items-center gap-2 mt-0.5 overflow-hidden">
                                                    {opt.badge && (
                                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-600 shrink-0 uppercase tracking-wider">
                                                            {opt.badge}
                                                        </span>
                                                    )}
                                                    {opt.subtitle && (
                                                        <span className="text-[11px] text-slate-500 truncate min-w-0">
                                                            {opt.subtitle}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableCombobox;
